import { Link } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { useFilamentText } from '../../../hooks/useFilamentText';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const PCPage = () => {
  const { isEnglish, getMaterialData, normalizedSlug, ...TEXT } = useFilamentText('pc');
  const materialData = getMaterialData('pc');

  const breadcrumbItems = [
    { label: isEnglish ? 'Home' : 'ホーム', path: '/' },
    { label: isEnglish ? 'Filament Data' : 'フィラメントデータ', path: '/filament' },
    { label: 'PC', path: '/filament/pc' }
  ];

  const filamentId = 'pc';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8" data-lang={isEnglish ? 'en' : 'ja'} data-filament-key={normalizedSlug}>
            {/* タイトル */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                PC
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="pc" />

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.classification}</span>
                      <span className="difficulty-advanced px-3 py-1 rounded text-sm font-semibold">{TEXT.advanced}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.baseResin}</span>
                      <span className="text-white">{materialData?.baseResin}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{isEnglish ? 'Key Features' : '主な特徴'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    {materialData?.features.map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{TEXT.item}</th>
                      <th className="px-4 py-3 text-left">{TEXT.recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{TEXT.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">270-310°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'High-temp nozzle required' : '高温対応ノズル必須'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.bedTemp}</td>
                      <td className="px-4 py-3 text-white">110-130°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'High-temp bed required' : '高温ベッド必須'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.fanSetting}</td>
                      <td className="px-4 py-3 text-white">0%</td>
                      <td className="px-4 py-3">{isEnglish ? 'No cooling' : '冷却厳禁'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.printSpeed}</td>
                      <td className="px-4 py-3 text-white">20-40mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? 'Slow for stability' : '低速で安定印刷'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">0.4-0.6mm</td>
                      <td className="px-4 py-3">{isEnglish ? 'Hardened steel' : '硬化スチール製'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{isEnglish ? 'Chamber Temp' : 'チャンバー温度'}</td>
                      <td className="px-4 py-3 text-white">60-80°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Enclosed chamber required' : '密閉チャンバー必須'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{TEXT.caution}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Drying required, high-temp equipment needed' : '乾燥必須、高温対応機材必要'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 応力-ひずみグラフ（CSV自動探索） */}
            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="PC"
            />

            {/* 派生・バリエーション */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.derivatives}
              </h2>
              <p className="text-metal-light mb-6">
                {materialData?.derivatives}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="bg-metal p-4 rounded border-l-2 border-accent">
                  <h3 className="text-white font-semibold mb-2">PC</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Standard PC. Extremely high heat resistance and strength, industrial-grade performance.' : '標準PC。極めて高い耐熱性と強度、工業グレードの性能。'}</p>
                </div>
                <Link 
                  to="/filament/pc-cf" 
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">PC-CF</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Carbon fiber reinforced. Aerospace-grade performance, extremely expensive.' : 'カーボン繊維強化。航空宇宙グレードの性能、極めて高価。'}</p>
                </Link>
                <Link 
                  to="/filament/pc-gf" 
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">PC-GF</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Glass fiber reinforced. Maximum dimensional stability and strength.' : 'ガラス繊維強化。寸法安定性と強度を極限まで高めた材料。'}</p>
                </Link>
              </div>
            </div>

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '向いている造形'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    {materialData?.suitableUses.map((use, idx) => (
                      <li key={idx}>• {use}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : '長所・短所'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : '長所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.pros}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : '短所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.cons}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                {materialData?.precautions.map((precaution, idx) => (
                  <p key={idx}>• {precaution}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PCPage;
